import React, { useEffect, useState } from 'react';
import { RadioGroup } from '@headlessui/react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import { useHistory, useParams } from 'react-router-dom';
import {
    askAndGetConnectedMediaDevices,
    filterMediaDevicesBySpecificKind,
} from 'flow/utils/media-utils';
import {
    setPreferredMicrophoneId,
    setConnectedMediaDevices,
} from 'flow/actions/headset-actions';

import { ExtensionKey } from 'flow/types/EntityKeys';

import { routes } from 'flow/types/routes';

import Page from './Page';
import SettingSection from 'flow/components/Setting/SettingSection';
import DevicePickerRow from './DevicePickerRow';
import { ConnectedMediaDevice } from 'flow/types/Headset';
import { generatePath } from 'flow/utils/router-utils';

const Microphone = () => {
    const dispatch = useAppDispatch();
    const { i18next } = useAppContext();
    const history = useHistory();
    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();

    const [error, setError] = useState<any>('');

    const preferredMicrophoneId = useAppSelector((state) =>
        state.headsets.get('preferredMicrophoneId'),
    );

    const microphones = useAppSelector((state) =>
        filterMediaDevicesBySpecificKind(
            state.headsets.get('connectedMediaDevices'),
            'audioinput',
        ),
    );

    const selectDefaultMicrophone = microphones.find(
        (microphone: ConnectedMediaDevice) =>
            microphone?.deviceId === 'default',
    );
    const selectedMicrophone =
        preferredMicrophoneId || selectDefaultMicrophone?.deviceId;

    useEffect(() => {
        askAndGetConnectedMediaDevices(true, false)
            .then((devices) => dispatch(setConnectedMediaDevices(devices)))
            .catch((error) => setError(error));
    }, []);

    const getErrorMessage = () => {
        const errorName = error.name || error;

        if (errorName === 'NotAllowedError') {
            return i18next.t('Access to microphone devices denied');
        }

        if (errorName === 'PermissionDismissedError') {
            return i18next.t('Access to microphone devices must be given');
        }

        return `An error occurred: ${errorName}`;
    };

    return (
        <Page
            header="media.settings.audioinput"
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.softphone.audio-in-out'], {
                        extensionKey,
                    }),
                )
            }
        >
            <SettingSection>
                {i18next.t('media.settings.audioinput')}
            </SettingSection>

            {error ? (
                <div className="text-red">{getErrorMessage()}</div>
            ) : (
                <RadioGroup
                    value={selectedMicrophone ?? null}
                    aria-label={i18next.t('media.settings.audioinput')}
                    onChange={(deviceId: string) =>
                        dispatch(setPreferredMicrophoneId(deviceId))
                    }
                >
                    {microphones?.map((mic: ConnectedMediaDevice) => (
                        <DevicePickerRow
                            key={mic.deviceId}
                            deviceId={mic.deviceId}
                            label={mic.label || i18next.t('status.unknown')}
                            selected={mic.deviceId === selectedMicrophone}
                        />
                    ))}
                </RadioGroup>
            )}
        </Page>
    );
};

export default Microphone;
